import { memo, useCallback, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import {
  AlertTriangle,
  CalendarDays,
  Check,
  ChevronDown,
  GripVertical,
  UserPlus,
} from 'lucide-react';
import { type ProjectMemberView, type TaskPriority, type TaskView } from '@apm/contracts';
import { cn } from '../../../lib/utils';
import { Avatar } from '../../../components/ui/avatar';
import { ProgressQuickAction } from '../../../components/ui/progress-quick-action';
import { TaskTypeTag } from '../../../components/ui/task-type-tag';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { priorityToneOf } from '../status-color';
import { isOffPlan } from '../../tasks/off-plan';
import { AssigneeMenu } from '../../tasks/components/assignee-picker';
import { RecurringMark } from '../../tasks/components/recurring-mark';
import { StoryPointBadge } from '../../tasks/components/story-point-badge';
import { GitTaskMarkBadge } from '../../git/components/git-task-mark';
import { ProgressReviewBadge } from '../../git/components/progress-review-badge';
import { completedOn, isCompletedLate, todayIso } from '../../../lib/overdue';
import { displayDate, displayDayMonth } from '../../../lib/date';
import { DatePicker } from '../../../components/ui/date-picker';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';

/** Cao xuống thấp — cùng thứ tự với bảng Danh sách, để hai menu không lệch nhau. */
const PRIORITIES: readonly TaskPriority[] = ['urgent', 'high', 'medium', 'low'];

interface TaskCardProps {
  readonly task: TaskView;
  readonly onProgress?: (progressPct: number) => void;
  /** Mở drawer chi tiết (bình luận / lịch sử / tệp) — APM-504c. */
  readonly onOpen?: () => void;
  /**
   * Đổi độ ưu tiên ngay trên thẻ — APM-BOARD-k.
   *
   * Bỏ trống ⇒ nhãn ưu tiên là chữ, không phải nút. Cùng luật với `FillCell` ở
   * bảng Danh sách: không sửa được thì **không phải một nút**, vì một nút bị tắt
   * mời người ta thử bấm rồi không nói vì sao không được.
   */
  readonly onPriority?: (priority: TaskPriority) => void;
  /**
   * Đổi deadline ngay trên thẻ — người dùng yêu cầu 2026-09-23.
   *
   * Chỉ truyền khi người xem **lập kế hoạch được** (`project:update`, tức PM):
   * dời một hạn là dời kế hoạch của cả chuỗi phía sau (APM-CHANGE-b), khác với
   * báo tiến độ hay đổi ưu tiên. Bỏ trống ⇒ chip hạn là nhãn, không phải nút.
   */
  readonly onDueDate?: (dueDate: string) => void;
  /**
   * Giao lại việc ngay trên thẻ — người dùng yêu cầu 2026-10-08. `null` = bỏ giao.
   *
   * Chỉ truyền khi người xem có `task:assign` (PM). Bỏ trống ⇒ chân thẻ là
   * avatar + tên như cũ, không phải nút — cùng luật với `onPriority`.
   */
  readonly onAssign?: (assigneeId: string | null) => void;
  /** Thành viên dự án cho danh sách chọn — chỉ có khi `onAssign` có. */
  readonly members?: readonly ProjectMemberView[];
  /** Thẻ đang bay theo con trỏ trong `DragOverlay` — không gắn sortable. */
  readonly overlay?: boolean;
  /** Cột Backlog: cho chọn nhiều việc rồi đưa cả lô vào sprint. */
  readonly selected?: boolean;
  readonly onSelect?: (selected: boolean) => void;
  /**
   * Kéo được hay không — APM-CHANGE-b.
   *
   * Kéo một thẻ trên board là một thao tác **lập kế hoạch**: nó đổi trạng thái,
   * đổi thứ tự, và ở cột backlog thì đổi cả sprint. Chỉ PM làm được (xem
   * `TasksPanel`). Người khác vẫn mở popup và báo tiến độ, đóng việc như cũ —
   * cái mất đi là cú kéo, không phải công việc.
   *
   * Khoá ở `useSortable` chứ không chỉ bỏ qua ở `onDragEnd`: thẻ vẫn nhấc lên
   * được thì người dùng kéo cả quãng rồi mới thấy nó bật về chỗ cũ, và không gì
   * nói cho họ biết vì sao.
   */
  readonly draggable?: boolean;
}

/**
 * Một thẻ việc trên board.
 *
 * ## Bố cục: hai mép bằng nhau, không có máng trái — APM-BOARD-k
 *
 * Thẻ này đã sai bố cục theo **hai** cách nối tiếp nhau, và cách thứ hai chỉ lộ
 * ra sau khi sửa cách thứ nhất.
 *
 * Ban đầu thẻ có hai mép trái: tiêu đề và hàng chip nằm trong cột nội dung
 * (thụt vào sau hộp chọn và tay nắm kéo), còn thanh tiến độ, nhãn trạng thái và
 * avatar lại là anh em của **cả hàng đó** nên chúng bắt đầu từ mép padding của
 * thẻ. Nửa trên thụt vào, nửa dưới thò ra.
 *
 * Dồn tất cả vào một cột nội dung thì hết so le, nhưng **cả khối** vẫn bị đẩy
 * sang phải: tay nắm chiếm một máng trái cố định, nên mép trái là `p-3` cộng
 * thêm máng (~24px) trong khi mép phải chỉ có `p-3`. Thẻ lệch hẳn một bên, và
 * bề ngang vốn đã hẹp (cột `w-72`) mất thêm chỗ cho một cái nút.
 *
 * Nay **không còn máng**. Tay nắm và hộp chọn nằm ở cụm góc phải của hàng đầu —
 * chúng là điều khiển, không phải nội dung, nên chúng không được quyền định ra
 * mép trái của mọi dòng bên dưới. Nội dung chiếm trọn bề ngang, và `p-3` là
 * khoảng cách duy nhất ở cả bốn phía.
 *
 * ## Nhịp dọc
 *
 * `space-y-2` cho mọi khối. `ProgressQuickAction` tự mang `mt-2`, đúng bằng
 * bước ấy: cùng một khai báo trên cùng một phần tử nên hai chỗ không cộng dồn.
 * Đó là lý do dùng `space-y` chứ không phải `gap` — `gap` thì cộng thêm thật.
 *
 * ## Hai hàng thông tin, hai vai trò khác nhau
 *
 * Hàng nhận dạng (mã việc + loại việc) nói **thẻ này là cái gì**; hàng chip bên
 * dưới nói **thuộc tính của nó**. Gộp cả năm mẩu vào một hàng như trước thì mã
 * việc — một chuỗi trần không khung — đứng lẫn giữa các chip có nền, và hàng đó
 * đọc như một đống mẩu rời.
 */
type DraggableBits = ReturnType<typeof useDraggable>;

interface TaskCardBodyProps extends TaskCardProps {
  readonly setNodeRef: ((node: HTMLElement | null) => void) | undefined;
  readonly isDragging: boolean;
  readonly attributes: DraggableBits['attributes'];
  readonly listeners: DraggableBits['listeners'];
}

/**
 * Lớp mỏng: gắn dnd-kit, rồi giao phần nặng cho `TaskCardBody` — APM-BOARD-perf4.
 *
 * ## ⚠️ `useDraggable` + `useDroppable`, KHÔNG phải `useSortable`
 *
 * Đây là thay đổi đã cắt được phần lag còn lại, và lý do nằm trong mã nguồn
 * dnd-kit chứ không ở đâu khác:
 *
 * - `DndContext` tính `collisions` **trong thân render, không memo** — một mảng
 *   mới ở mỗi lần nó vẽ lại, tức mỗi lần con trỏ nhích.
 * - `collisions` nằm trong **public context**.
 * - `SortableContext` đọc public context (`useDndContext()`), nên nó vẽ lại ở
 *   mỗi lần con trỏ nhích; giá trị context của chính nó cũng đổi theo.
 * - ⇒ **mọi** `useSortable` vẽ lại ở mỗi lần con trỏ nhích. Sáu mươi thẻ, sáu
 *   mươi lần, mỗi khung hình.
 *
 * `useDraggable` và `useDroppable` thì chỉ đọc **internal context**, và context
 * ấy không đổi khi con trỏ nhích — chỉ đổi khi `over` đổi (tức khi chỗ rơi đổi
 * thật). Đó là đúng tần suất mà board này cần.
 *
 * Bỏ `useSortable` không mất gì: board tự dựng khung trống (`DropSlot`) và tự
 * tính chỗ rơi (`dropIndex`), nên phần "dẹp chỗ" của `SortableContext` đã bị
 * tắt từ APM-BOARD-perf3 rồi.
 *
 * ## Vì sao vẫn tách thân thẻ ra `memo`
 *
 * `over` đổi vài chục lần trong một cú kéo, và mỗi lần ấy internal context đổi
 * ⇒ lớp mỏng này vẫn vẽ lại. Thân thẻ thì không: prop của nó không đổi.
 */
export function TaskCard(props: TaskCardProps): ReactNode {
  const { task, overlay = false, draggable = true } = props;
  const disabled = overlay || !draggable;

  const drag = useDraggable({ id: task.id, disabled });
  // Thẻ vừa là thứ kéo được vừa là **đích thả** — `boardCollision` tra vị trí
  // từng thẻ để biết chen vào đâu. Không đăng ký droppable thì cả cột chỉ có
  // một đích duy nhất, và chỗ rơi luôn là "cuối cột".
  const drop = useDroppable({ id: task.id, disabled: overlay });

  const setNodeRef = useCallback(
    (node: HTMLElement | null) => {
      drag.setNodeRef(node);
      drop.setNodeRef(node);
    },
    [drag.setNodeRef, drop.setNodeRef],
  );

  return (
    <TaskCardBody
      {...props}
      setNodeRef={overlay ? undefined : setNodeRef}
      isDragging={drag.isDragging}
      attributes={drag.attributes}
      listeners={drag.listeners}
    />
  );
}

const TaskCardBody = memo(function TaskCardBody({
  task,
  onProgress,
  onOpen,
  onPriority,
  onDueDate,
  onAssign,
  members,
  overlay = false,
  selected = false,
  onSelect,
  setNodeRef,
  isDragging,
  attributes,
  listeners,
}: TaskCardBodyProps): ReactNode {
  const { t } = useTranslation();

  /**
   * Thẻ đang nhấc **biến mất khỏi bố cục** — APM-BOARD-perf3.
   *
   * Bản rõ nét đang bay theo con trỏ ở `DragOverlay`, còn chỗ trống thì cột tự
   * chèn ở **đúng chỗ sắp rơi vào** (`DropSlot`). Nếu chỗ này cũng vẽ một khung
   * nữa thì kéo sang cột khác sẽ thành hai khung: một ở cột cũ, một ở cột mới.
   *
   * Vẫn giữ một node cao 0px thay vì trả `null`: `setNodeRef` phải gắn vào một
   * phần tử có thật, nếu không dnd-kit mất node của thứ đang kéo giữa chừng.
   */
  if (isDragging && !overlay) {
    return <div ref={setNodeRef} className="h-0 overflow-hidden" aria-hidden />;
  }

  /** Quá hạn = có hạn, hạn đã qua, và **chưa xong** — cùng luật với bảng Danh sách. */
  const late = task.dueDate !== null && task.dueDate < todayIso() && task.statusCategory !== 'done';
  /**
   * Xong rồi nhưng xong **sau** hạn — người dùng yêu cầu 2026-09-30: thẻ ở cột
   * hoàn thành cũng phải cho thấy nó đã trễ, cùng một kiểu đỏ đậm với thẻ quá
   * hạn. Không gộp vào `late`: `late` còn là "việc cần xử lý" ở bộ lọc Thời hạn.
   */
  const doneLate = isCompletedLate(task.dueDate, task.statusCategory, task.actualEndAt);
  const dueTitle = doneLate
    ? t('board.cardDueDoneLate', {
        value: displayDate(task.dueDate),
        done: displayDate(completedOn(task.statusCategory, task.actualEndAt)),
      })
    : t(late ? 'board.cardDueLate' : 'board.cardDue', { value: displayDate(task.dueDate) });

  /** "Cần làm": hiện tiến độ nhưng khoá — xem chú thích ở chỗ dùng bên dưới. */
  const notStarted = task.statusCategory === 'todo';
  const showsProgress =
    task.statusCategory === 'in_progress' ||
    task.checklist !== null ||
    (notStarted && task.progressPct > 0);

  return (
    <article
      ref={setNodeRef}
      className={cn(
        'space-y-2 rounded-DEFAULT border border-border bg-surface p-3 text-sm shadow-sm',
        overlay && 'rotate-2 shadow-lg',
        selected && 'ring-2 ring-primary',
      )}
    >
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          {/* Hàng nhận dạng. */}
          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <span className="shrink-0 font-mono" title={t('board.cardKey')}>
              {task.key}
            </span>
            {/* Mốc không có loại việc — APM-TASK-ms2a. */}
            {task.taskTypeId !== null && task.taskTypeName !== null ? (
              <span className="min-w-0 truncate" title={t('board.cardType')}>
                <TaskTypeTag
                  id={task.taskTypeId}
                  name={task.taskTypeName}
                  color={task.taskTypeColor}
                />
              </span>
            ) : null}
            {/* Sinh từ mẫu việc định kỳ — APM-REC-c. */}
            {task.recurrenceId ? <RecurringMark /> : null}
          </div>

          {/* Nút chứ không phải `onClick` trên cả thẻ: cả thẻ vừa là vùng kéo
              thả vừa chứa slider tiến độ, nên click ở đâu cũng mở chi tiết sẽ
              bật drawer mỗi lần người dùng nhấc tay sau khi kéo. */}
          {onOpen ? (
            <button
              type="button"
              onClick={onOpen}
              className="mt-0.5 block w-full truncate text-left font-medium hover:underline"
            >
              {task.title}
            </button>
          ) : (
            <p className="mt-0.5 truncate font-medium">{task.title}</p>
          )}
        </div>

        {/*
          Cụm góc phải: dấu bị chặn, hộp chọn, tay nắm kéo.

          Chúng ở **cùng một chỗ** vì chúng cùng một loại — thứ gắn vào cả thẻ,
          không phải một trường dữ liệu của việc. Để bên phải thì mép trái của
          mọi dòng bên dưới không bị chúng đẩy đi, và đó là toàn bộ lý do cụm này
          tồn tại.

          `size-4` tường minh cho từng icon: mặc định của lucide là 24px, to
          ngang hàng chữ tiêu đề.
        */}
        <div className="flex shrink-0 items-center gap-1">
          {task.isBlocked ? (
            <AlertTriangle
              className="size-4 text-rag-red"
              aria-label={
                task.blockReason === null
                  ? t('board.cardBlockedNoReason')
                  : t('board.cardBlocked', { reason: task.blockReason })
              }
            />
          ) : null}

          {onSelect && !overlay ? (
            <input
              type="checkbox"
              checked={selected}
              onChange={(event) => onSelect(event.target.checked)}
              aria-label={t('board.selectTask', { key: task.key })}
              className="ios-check ios-check-select"
            />
          ) : null}

          {overlay ? null : (
            <button
              type="button"
              className="cursor-grab text-muted-foreground active:cursor-grabbing"
              aria-label={t('board.dragCard', { key: task.key })}
              {...attributes}
              {...listeners}
            >
              <GripVertical aria-hidden className="size-4" />
            </button>
          )}
        </div>
      </div>

      {/*
        Hàng chip — APM-BOARD-i.

        Trước đây ba mẩu thông tin này là chữ xám trôi cạnh nhau, phân biệt
        được bằng cách đọc. Chip có nền và một **vạch màu bên trái** thì mỗi
        mẩu thành một vật riêng, và vạch màu nói luôn giá trị mà không cần
        đọc chữ — đúng cách một thẻ trên board được liếc chứ không được đọc.

        Mỗi chip mang `title` nói **nó là loại dữ liệu gì** (APM-BOARD-k):
        một vạch màu cho biết "cao" nhưng không cho biết "cao" là cao cái gì,
        và đó là câu hỏi chỉ xuất hiện một lần cho mỗi người dùng mới.
      */}
      <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
        {/*
          Việc **ngoài kế hoạch PM** — APM-TASK-creator.

          Chỉ khi `=== false`. `null` là *chưa biết* (Replay, bản nháp), và
          một dấu dán lên chỗ không có dữ liệu nói sai về cả dự án.
        */}
        {isOffPlan(task) ? (
          <Chip
            accent="var(--color-rag-amber)"
            className="text-rag-amber"
            title={
              task.createdByName === null
                ? t('board.createdByOtherUnknown')
                : t('board.createdByOtherTitle', { name: task.createdByName })
            }
          >
            <UserPlus aria-hidden className="size-3" />
            {t('board.createdByOther')}
          </Chip>
        ) : null}

        <PriorityChip
          priority={task.priority}
          {...(onPriority && !overlay && (task.canEdit ?? true) ? { onPick: onPriority } : {})}
        />

        {task.dueDate === null ? null : onDueDate && !overlay ? (
          <DueChip
            dueDate={task.dueDate}
            late={late || doneLate}
            {...(doneLate ? { lateTitle: dueTitle } : {})}
            onPick={onDueDate}
          />
        ) : (
          <Chip
            accent={late || doneLate ? 'var(--color-rag-red)' : undefined}
            className={late || doneLate ? 'font-semibold text-rag-red' : undefined}
            title={dueTitle}
          >
            <CalendarDays aria-hidden className="size-3" />
            {displayDayMonth(task.dueDate)}
          </Chip>
        )}

        {/*
          Ô SP luôn hiện, kể cả chưa có; bấm xem nguồn (so với việc cũ hay tự điền) và
          ước lượng lại — người dùng yêu cầu 2026-11-02 (`StoryPointBadge`). Cùng hình
          chip với mức ưu tiên và hạn ngay cạnh (`variant="chip"` + `CHIP_BASE`).
        */}
        <StoryPointBadge task={task} variant="chip" className={CHIP_BASE} />

        {/* MR đang mở + chấm CI — APM-GIT-i, §8.2. Đọc từ `GitTaskMarksProvider` của board. */}
        <GitTaskMarkBadge taskId={task.id} />
        {/* AI rà commit đã báo thấy thấp, PM chưa xem — APM-PCRV. Đọc từ provider của board. */}
        <ProgressReviewBadge taskId={task.id} />
      </div>

      {/*
        Chỉ cột "đang làm" mới **sửa** được tiến độ.

        Ở "hoàn thành" thì % đã là 100 và thanh trượt chỉ còn một cách dùng là
        **kéo lùi** — tức là nói việc chưa xong, mà cách đúng để nói điều đó là
        kéo thẻ về cột trước. Cột đó hiện trạng thái có màu thay cho thanh
        trượt: cùng một chỗ trên thẻ, nhưng nói thứ đang đúng ở đó.

        Ở "cần làm" thì **không có nhãn nào**: cột đã mang đúng cái tên ấy ngay
        phía trên, nên một nhãn "Cần làm" trên từng thẻ chỉ lặp lại nó mấy chục
        lần. Nhưng một việc bị đẩy ngược từ "đang làm" về đây vẫn giữ % đã làm
        được, và con số đó thì đáng thấy — nó nói việc này đã đi được bao xa
        trước khi quay lại. Nên: hiện thanh chỉ-đọc khi có gì để hiện, còn việc
        chưa động tới (0%, không checklist) thì chỗ này để trống.
      */}
      {showsProgress ? (
        onProgress && !overlay ? (
          <ProgressQuickAction
            value={task.progressPct}
            checklist={task.checklist}
            notStarted={notStarted}
            // Backlog Agile: thấy % nhưng không sửa — cửa thật ở server.
            inBacklog={task.inBacklog === true}
            // Task cha lấy % từ subtask, không nhập tay được (spec 9.1) —
            // server trả 400, nên đừng mời người dùng bấm vào một thứ chắc
            // chắn hỏng.
            disabled={task.subtaskCount > 0}
            onChange={onProgress}
          />
        ) : null
      ) : task.statusCategory === 'done' ? (
        <StatusChip category={task.statusCategory} name={task.statusName} />
      ) : null}

      {/* Chân thẻ — APM-BOARD-k.
          Avatar **và** tên. Trước đây chỉ có avatar, với lý do một dòng tên
          chiếm cả bề ngang thẻ; nhưng ở bố cục mới chân thẻ là một hàng
          riêng vốn đã trống, và hai chữ cái đứng một mình dưới góc trái là
          đúng thứ mảnh vụn mà lần dọn này phải bỏ. Tên `truncate` nên nó
          không đẩy gì cả. */}
      {onAssign && !overlay ? (
        <AssigneeButton
          taskKey={task.key}
          assigneeId={task.assigneeId}
          assigneeName={task.assigneeName}
          members={members ?? []}
          onPick={onAssign}
        />
      ) : task.assigneeName ? (
        <div className="flex min-w-0 items-center gap-1.5">
          <Avatar name={task.assigneeName} size="sm" />
          <span className="min-w-0 truncate text-xs text-muted-foreground">
            {task.assigneeName}
          </span>
        </div>
      ) : null}
    </article>
  );
});

/**
 * Chân thẻ **bấm được**: rê vào vùng tên thì nó sáng lên, bấm thì bung danh
 * sách thành viên để giao lại việc — người dùng yêu cầu 2026-10-08.
 *
 * Nội dung là đúng `AssigneeMenu` của cột Phụ trách ở bảng Danh sách và của
 * form sửa việc: gõ tên để lọc, Enter chọn người đầu tiên, có cả "chưa giao".
 * Ba chỗ cùng hỏi "giao cho ai" thì dùng một danh sách, nếu không chúng sẽ
 * trôi khỏi nhau.
 *
 * `Popover` của Radix, cùng lý do với `PriorityChip`: nó render qua `Portal`
 * nên vùng cuộn của cột không cắt mất — chân thẻ là thứ nằm sát đáy cột nhất.
 *
 * Việc **chưa giao ai** cũng có nút này ("Giao việc"): với người phân công, đó
 * chính là những thẻ cần bấm nhất. Người không có quyền thì thẻ chưa giao vẫn
 * không có chân, như trước.
 */
function AssigneeButton({
  taskKey,
  assigneeId,
  assigneeName,
  members,
  onPick,
}: {
  readonly taskKey: string;
  readonly assigneeId: string | null;
  readonly assigneeName: string | null;
  readonly members: readonly ProjectMemberView[];
  readonly onPick: (assigneeId: string | null) => void;
}): ReactNode {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const label = assigneeName
    ? t('board.cardAssigneePick', { key: taskKey, name: assigneeName })
    : t('board.cardAssigneeEmpty', { key: taskKey });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title={label}
          aria-label={label}
          className={cn(
            // `-mx-1` bù `px-1`: nền hover rộng ra hai bên mà avatar vẫn thẳng
            // mép trái với mọi dòng phía trên của thẻ.
            'group/assignee -mx-1 flex max-w-full items-center gap-1.5 rounded-control px-1 py-0.5 text-left',
            'cursor-pointer transition-colors hover:bg-muted data-[state=open]:bg-muted',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
          )}
        >
          <Avatar name={assigneeName} size="sm" />
          <span
            className={cn(
              'min-w-0 truncate text-xs text-muted-foreground group-hover/assignee:text-foreground',
              assigneeName === null && 'italic',
            )}
          >
            {assigneeName ?? t('board.cardAssign')}
          </span>
          {/* Mũi tên chỉ hiện lúc rê vào / focus: nó nói "đây là một nút" đúng
              lúc cần, mà không thêm một dấu vào mọi thẻ của board. */}
          <ChevronDown
            aria-hidden
            className="size-3 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover/assignee:opacity-70 group-focus-visible/assignee:opacity-70 group-data-[state=open]/assignee:opacity-70"
          />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-2">
        <AssigneeMenu
          members={members}
          current={assigneeId}
          onPick={(next) => {
            if (next !== assigneeId) onPick(next);
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

/**
 * Nhãn ưu tiên — đọc được, và **bấm đổi được** khi có quyền (APM-BOARD-k).
 *
 * ## Vì sao Radix chứ không phải menu tự dựng như `FillCell`
 *
 * `FillCell` ở bảng Danh sách định vị menu bằng `absolute`. Trên board thì
 * không được: danh sách thẻ của một cột nằm trong `overflow-y-auto`, và một
 * phần tử `absolute` bên trong vùng cuộn **bị cắt** ở mép cột — menu sẽ mất một
 * nửa, hoặc biến mất hẳn với thẻ cuối cột. `DropdownMenuContent` render qua
 * `Portal` nên nó thoát khỏi vùng cuộn, và kèm theo bốn thứ menu tự viết hay
 * thiếu: Escape để đóng, mũi lên/xuống, bấm ra ngoài, và trả focus về nút mở.
 *
 * Không sửa được thì đây là một `<span>`, không phải `<button>` bị tắt — cùng
 * luật với `FillCell`.
 */
function PriorityChip({
  priority,
  onPick,
}: {
  readonly priority: TaskPriority;
  readonly onPick?: (priority: TaskPriority) => void;
}): ReactNode {
  const { t } = useTranslation();
  const tone = priorityToneOf(priority);
  const label = t(`priority.${priority}`);

  if (!onPick) {
    return (
      <Chip accent={tone.background} title={t('board.cardPriority', { value: label })}>
        {label}
      </Chip>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          title={t('board.cardPriorityPick', { value: label })}
          aria-label={t('board.cardPriorityPick', { value: label })}
          className={cn(
            CHIP_BASE,
            'border-l-[3px] pl-1.5',
            // Hover **và** focus: chip này là một nút nhưng trông như một nhãn,
            // nên nó phải tự nói ra điều đó lúc con trỏ đi qua.
            'cursor-pointer transition-colors hover:bg-muted-foreground/20',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
          )}
          style={{ borderLeftColor: tone.background }}
        >
          {label}
          <ChevronDown aria-hidden className="size-3 opacity-60" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" className="min-w-40">
        {PRIORITIES.map((value) => (
          <DropdownMenuItem
            key={value}
            onSelect={() => {
              if (value !== priority) onPick(value);
            }}
            className="text-xs"
          >
            <span
              aria-hidden
              className="size-3 shrink-0 rounded-sm"
              style={{ backgroundColor: priorityToneOf(value).background }}
            />
            <span className="min-w-0 flex-1 truncate">{t(`priority.${value}`)}</span>
            {value === priority ? (
              <Check aria-hidden className="size-3.5 shrink-0 text-primary" />
            ) : null}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Chip hạn bấm được — mở lịch để chọn lại ngày (người dùng yêu cầu 2026-09-23).
 *
 * Popover của Radix, cùng lý do với `PriorityChip`: nó render qua `Portal` nên
 * không bị vùng cuộn của cột cắt mất, và có sẵn Escape / bấm ra ngoài để đóng.
 */
function DueChip({
  dueDate,
  late,
  lateTitle,
  onPick,
}: {
  readonly dueDate: string;
  /** Quá hạn, **hoặc** đã xong mà xong trễ — cả hai cùng đỏ đậm. */
  readonly late: boolean;
  /** Câu nói vì sao đỏ, khi đỏ vì **xong trễ** — không có nó thì một thẻ ở cột
   *  hoàn thành mà hạn đỏ trông như lỗi hiển thị. */
  readonly lateTitle?: string;
  readonly onPick: (dueDate: string) => void;
}): ReactNode {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const pick = t('board.cardDuePick', { value: displayDate(dueDate) });
  const label = lateTitle ? `${lateTitle} — ${pick}` : pick;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title={label}
          aria-label={label}
          className={cn(
            CHIP_BASE,
            'border-l-[3px] pl-1.5',
            'cursor-pointer transition-colors hover:bg-muted-foreground/20',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
            late && 'font-semibold text-rag-red',
          )}
          style={{ borderLeftColor: late ? 'var(--color-rag-red)' : 'transparent' }}
        >
          <CalendarDays aria-hidden className="size-3" />
          {displayDayMonth(dueDate)}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-2">
        <DatePicker
          aria-label={label}
          value={dueDate}
          onChange={(next) => {
            if (next === '' || next === dueDate) return;
            onPick(next);
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

/** Hình dạng chung của chip — dùng cho cả `<span>` tĩnh lẫn nút ưu tiên. */
const CHIP_BASE =
  'inline-flex items-center gap-1 overflow-hidden rounded-control bg-muted py-0.5 pr-1.5 text-[11px]';

/**
 * Mẩu thông tin nhỏ trên thẻ, có **vạch màu bên trái** — APM-BOARD-i.
 *
 * Vạch chứ không phải nền tô kín: trên một thẻ đã có nhãn loại việc và có thể
 * có thanh tiến độ, ba chip nền đậm sẽ tranh nhau với chính tiêu đề. Vạch 3px
 * đủ để mắt bắt được giá trị mà không kéo sự chú ý khỏi tên việc.
 *
 * `accent` để trống thì chip trung tính — dùng cho thứ không có thang màu nào
 * (điểm story).
 */
function Chip({
  accent,
  className,
  title,
  children,
}: {
  readonly accent?: string | undefined;
  readonly className?: string | undefined;
  /** Nói mẩu này là **loại dữ liệu gì** — xem chú thích ở hàng chip. */
  readonly title?: string | undefined;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <span
      title={title}
      className={cn(CHIP_BASE, accent ? 'border-l-[3px] pl-1.5' : 'pl-1.5', className)}
      style={accent ? { borderLeftColor: accent } : undefined}
    >
      {children}
    </span>
  );
}

/**
 * Trạng thái dạng nhãn màu — nay **chỉ** cột "hoàn thành" dùng tới.
 *
 * Cột "cần làm" đã bỏ nhãn này: cột mang đúng cái tên ấy ngay phía trên, nên
 * lặp lại nó trên từng thẻ là chữ thừa. Giữ `category` làm tham số thay vì
 * hardcode màu xanh: nhãn vẫn là "trạng thái của thẻ này", không phải "thẻ này
 * đã xong" — chỗ gọi mới là chỗ quyết định khi nào nó xuất hiện.
 *
 * Màu ở đây **mang ý nghĩa** (khác nhãn loại việc, chỉ để phân biệt), nên nó
 * lấy từ token RAG. Tên trạng thái luôn đứng cạnh — người mù màu đỏ-xanh vẫn
 * đọc được (docs/frontend-guide.md §4).
 */
function StatusChip({
  category,
  name,
}: {
  category: TaskView['statusCategory'];
  name: string;
}): ReactNode {
  const tone =
    category === 'done' ? 'bg-rag-green/15 text-rag-green' : 'bg-rag-unknown/15 text-rag-unknown';

  return (
    <p>
      <span className={cn('inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium', tone)}>
        {name}
      </span>
    </p>
  );
}
